// Top-level React error boundary. In production it shows a calm,
// kid-friendly recovery card (no raw stack, no destructive wipe); in
// development it shows the full error + component/JS stack for repro.
//
// R-2: the previous version dumped a raw JS stack to kids AND its only
// recovery button called localStorage.clear() — which, because offline
// progress is local-first and never re-syncs, PERMANENTLY destroyed all
// unsynced attempts/milestones/unlocks. We now:
//   - never clear localStorage in production (data-safety);
//   - gate the raw stack dump + the destructive clear behind
//     import.meta.env.DEV;
//   - give a NON-LOOPING escape: a plain reload first, but if the same
//     crash recurs after a reload (a deterministic crash), offer a
//     "Go home" navigation instead of reloading the same broken route
//     forever. The loop counter lives in sessionStorage and is cleared
//     whenever the app mounts cleanly.
import React from "react";
import { logRender } from "../engine/sessionLog.ts";
import { captureRenderError } from "../lib/sentry";

const LOOP_KEY = "pw-eb-reloads";

function readLoopCount() {
  try { return Number(sessionStorage.getItem(LOOP_KEY) || "0") || 0; }
  catch { return 0; }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidMount() {
    // A clean mount means a prior reload recovered (or there was never a
    // crash) — reset the deterministic-crash loop counter.
    if (!this.state.error) {
      try { sessionStorage.removeItem(LOOP_KEY); } catch { /* */ }
    }
  }
  componentDidCatch(error, info) {
    this.setState({ info });
    // Count crashes this tab-session so we can detect a reload that didn't
    // help (a deterministic crash) and offer a non-looping escape instead.
    try {
      sessionStorage.setItem(LOOP_KEY, String(readLoopCount() + 1));
    } catch { /* */ }
    // P-OBSERVABILITY-A: surface render crashes in Sentry (no-op in dev /
    // when unconfigured).
    try { captureRenderError(error, String(info?.componentStack || "")); }
    catch { /* never let reporting crash the boundary */ }
    try {
      console.error("[PencilWise] render crashed:", error, info);
      logRender({
        id: "error-boundary",
        moduleId: "error",
        type: "render_crash",
        prompt: String(error?.message || error),
        correctAnswer: null,
        visualType: null,
        visualData: { stack: String(info?.componentStack || "") },
        responseMode: null,
        distractors: [],
      });
    } catch { /* never let logging itself crash the boundary */ }
  }
  render() {
    if (!this.state.error) return this.props.children;

    const isDev = import.meta.env.DEV;
    // >=2 crashes this tab-session means a plain reload already failed to
    // clear it → stop offering a reload that would just loop.
    const looping = readLoopCount() >= 2;

    if (!isDev) {
      // Production: calm, kid-friendly card. Never clears localStorage.
      return (
        <div data-app-crashed="true" style={{
          display: "flex", minHeight: "100dvh", alignItems: "center",
          justifyContent: "center", padding: "32px 20px",
          fontFamily: "system-ui, sans-serif", color: "var(--ink, #2A1F1A)",
          textAlign: "center",
        }}>
          <div style={{ maxWidth: 360 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }} aria-hidden="true">🐢</div>
            <h1 style={{ fontSize: 24, marginBottom: 10, fontWeight: 800 }}>
              Whoops! Let's try again
            </h1>
            <p style={{ fontSize: 16, lineHeight: 1.5, marginBottom: 20, opacity: 0.85 }}>
              {looping
                ? "That part is being stubborn. Let's head back to the start."
                : "Something got stuck. Tap the button to pick up where you left off."}
            </p>
            <button
              onClick={() => {
                if (looping) {
                  try { sessionStorage.removeItem(LOOP_KEY); } catch { /* */ }
                  // Non-destructive escape: leave the crashing route rather
                  // than reloading it again (no data wipe).
                  location.href = "/";
                } else {
                  location.reload();
                }
              }}
              style={{
                padding: "12px 22px", borderRadius: 14, minHeight: 44,
                background: "var(--cta, #C76A48)", color: "#fff", border: "none",
                fontFamily: "system-ui", fontWeight: 700, fontSize: 16, cursor: "pointer",
              }}
            >
              {looping ? "Go home" : "Try again"}
            </button>
          </div>
        </div>
      );
    }

    // Development: full diagnostics + the destructive reset (devs only).
    const msg = String(this.state.error?.message || this.state.error || "Unknown error");
    const stack = String(this.state.error?.stack || "");
    const compStack = String(this.state.info?.componentStack || "");
    return (
      <div data-app-crashed="true" style={{
        padding: "32px 20px", maxWidth: 720, margin: "40px auto",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: 13, lineHeight: 1.5, color: "var(--ink)",
      }}>
        <h1 style={{ fontSize: 22, marginBottom: 12, fontFamily: "system-ui" }}>Something went wrong (dev).</h1>
        <p style={{ marginBottom: 12, fontFamily: "system-ui", fontSize: 14 }}>
          The error below is what crashed. "Reset & reload" clears ALL local
          state — dev-only, since it destroys unsynced progress.
        </p>
        <pre style={{ background: "#FFF1EE", border: "1px solid #FFD0C7", padding: 14, borderRadius: 12, whiteSpace: "pre-wrap", overflow: "auto" }}>
          <strong>Error:</strong> {msg}
          {"\n\n"}<strong>Component stack:</strong>{compStack}
          {"\n\n"}<strong>JS stack:</strong>{"\n"}{stack}
        </pre>
        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <button
            onClick={() => location.reload()}
            style={{
              padding: "10px 18px", borderRadius: 12, background: "#2A1F1A",
              color: "#fff", border: "none", fontFamily: "system-ui",
              fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >Reload</button>
          <button
            onClick={() => {
              try { localStorage.clear(); } catch { /* */ }
              try { sessionStorage.removeItem(LOOP_KEY); } catch { /* */ }
              location.reload();
            }}
            style={{
              padding: "10px 18px", borderRadius: 12, background: "#FF6F61",
              color: "#fff", border: "none", fontFamily: "system-ui",
              fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >Reset &amp; reload (clears local state)</button>
        </div>
      </div>
    );
  }
}
